<script setup lang="ts">
import MainLayout from "@/layouts/MainLayout.vue";
import ProjectOverview from "@/components/Project/ProjectOverview.vue";
import { computed, onMounted, ref, watch } from "vue";
import client from "@/utils/axios";
import { useRoute } from "vue-router";
import { Project } from "@/types";
import { errorToast } from "@/utils/custom-toast";
import SecondaryPageHeader from "@/components/PageHeader/SecondaryPageHeader.vue";
import PhaseSidemenu from "@/components/Sidebar/Redesign/PhaseSidemenu.vue";
import SpinnerLoader from "@/components/Loaders/SpinnerLoader.vue";
import { useUserStore } from "@/stores/user";

const isLoading = ref(false);
const userStore = useUserStore();
let currentLanguage = computed(() => userStore.language);
let lastDatabaseTranslationUpdateTimestamp = computed(
  () => userStore.translationsUpdateTimestamp
);

const route = useRoute();
const id = route.params.id;
const projectDetails = ref<Project | null>(null);

// Fetch data
const getProjectDetails = async () => {
  isLoading.value = true;
  try {
    const response = await client.get(
      `/projects/${id}?sort_by=end&sort_order=desc&locale=${currentLanguage.value}`
    );
    projectDetails.value = response.data;
  } catch (error: any) {
    if (error.response && error.response.data) {
      errorToast(error.response.data?.message);
    }
  } finally {
    isLoading.value = false;
  }
};
onMounted(() => {
  getProjectDetails();
});
//

const salesforceLink = computed(() => {
  if (projectDetails.value) {
    return `https://smalt.lightning.force.com/lightning/r/Phase__c/${projectDetails.value?.notionId}/view`;
  }
  return null;
});

watch(
  currentLanguage,
  async (newLang: any, oldLang: any) => {
    if (newLang && oldLang && newLang !== oldLang) {
      await getProjectDetails();
    }
  },
  { immediate: true }
);
watch(
  lastDatabaseTranslationUpdateTimestamp,
  async (newTimestamp: any, oldTimestamp: any) => {
    if (newTimestamp && oldTimestamp && newTimestamp !== oldTimestamp) {
      await getProjectDetails();
    }
  },
  { immediate: true }
);
</script>

<template>
  <MainLayout>
    <div class="w-full">
      <SecondaryPageHeader
        :project-details="projectDetails"
        :salesforce-link="salesforceLink"
      />
    </div>
    <div
      class="w-full h-[calc(100vh-140px)] flex items-center justify-center"
      v-if="isLoading"
    >
      <SpinnerLoader />
    </div>
    <div v-else class="h-full flex relative">
      <PhaseSidemenu
        v-if="projectDetails"
        :project-details="projectDetails.phases"
      />
      <div class="w-full mt-[30px] mx-[20px]">
        <ProjectOverview
          :project-details="projectDetails"
          :is-loading="isLoading"
          @refresh="getProjectDetails"
        />
      </div>
    </div>
  </MainLayout>
</template>
